//
// useQnsDomainLookup — Phase 2 forward lookup (domain → address).
//
// Mirrors the design of useQnsResolution (Phase 1 reverse lookup):
//   - Calls /api/qns/lookup BFF route. No api-key in the browser.
//   - localStorage cache (7 days for hits, 1 min for misses) so repeat
//     searches resolve instantly.
//   - React Query in-memory cache for in-page de-duplication.
//   - Fails safe: any error returns owner:null, never throws.
//
// Feature flag: NEXT_PUBLIC_QNS_SEARCH_ENABLED must be "true" or the hook
// no-ops entirely. This is a separate flag from NEXT_PUBLIC_QNS_ENABLED so
// search can be killed independently of name display.
//
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import { getEnvValue } from 'configs/app/utils';

export interface QnsDomainLookup {
  domain: string;
  owner: string | null;
  domainType: 'paid' | 'free' | string | null;
}

const QNS_ENABLED = getEnvValue('NEXT_PUBLIC_QNS_ENABLED') === 'true';
const QNS_SEARCH_ENABLED = getEnvValue('NEXT_PUBLIC_QNS_SEARCH_ENABLED') === 'true';

// Mirrors server-side validation (nextjs/utils/qnsResolver.ts). Keep in sync.
// Consecutive-hyphen check is split out of the regex to keep the pattern
// simple enough for the lint regex-complexity rule.
const DOMAIN_RE = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]\.qie$/;

function matchesDomainPattern(normalized: string): boolean {
  if (!DOMAIN_RE.test(normalized)) return false;
  return !normalized.includes('--');
}

const LOCAL_STORAGE_KEY = 'qns-lookup-cache-v1';
const HIT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const MISS_TTL_MS = 60 * 1000; // 1 minute

interface PersistedEntry {
  owner: string | null;
  domainType: string | null;
  expiresAt: number;
}

let memoryCache: Record<string, PersistedEntry> = {};
let cacheLoaded = false;

function loadCache(): void {
  if (cacheLoaded || typeof window === 'undefined') return;
  cacheLoaded = true;
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, PersistedEntry>;
    const now = Date.now();
    memoryCache = Object.fromEntries(
      Object.entries(parsed).filter(([ , v ]) =>
        v && typeof v.expiresAt === 'number' && v.expiresAt > now,
      ),
    );
  } catch {
    memoryCache = {};
  }
}

function persistCache(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(memoryCache));
  } catch { /* quota exceeded or storage disabled — in-memory only */ }
}

function getCached(domain: string): PersistedEntry | undefined {
  loadCache();
  const entry = memoryCache[domain];
  if (!entry) return undefined;
  if (entry.expiresAt < Date.now()) {
    delete memoryCache[domain];
    return undefined;
  }
  return entry;
}

function setCached(domain: string, owner: string | null, domainType: string | null): void {
  loadCache();
  const ttl = owner ? HIT_TTL_MS : MISS_TTL_MS;
  memoryCache[domain] = { owner, domainType, expiresAt: Date.now() + ttl };
  persistCache();
}

export function isValidQnsDomain(input: string | undefined | null): input is string {
  return typeof input === 'string' && matchesDomainPattern(input.trim().toLowerCase());
}

/**
 * Quick check used by callers like SearchBar to decide if a query string
 * looks like a .qie domain. Returns the normalized form if so, else null.
 *
 * Importantly: this is a *syntactic* check only; it does NOT prove that the
 * domain is registered. The actual existence check happens via useQnsDomainLookup.
 */
export function normalizeQnsDomain(input: string | undefined | null): string | null {
  if (typeof input !== 'string') return null;
  const normalized = input.trim().toLowerCase();
  return matchesDomainPattern(normalized) ? normalized : null;
}

async function fetchLookup(domain: string, signal?: AbortSignal): Promise<QnsDomainLookup> {
  const empty: QnsDomainLookup = { domain, owner: null, domainType: null };
  try {
    const res = await fetch(`/api/qns/lookup?domain=${ encodeURIComponent(domain) }`, { signal });
    if (!res.ok) return empty;
    const data = await res.json() as Partial<QnsDomainLookup>;
    return {
      domain,
      owner: typeof data.owner === 'string' ? data.owner.toLowerCase() : null,
      domainType: typeof data.domainType === 'string' ? data.domainType : null,
    };
  } catch {
    return empty;
  }
}

async function queryFnWithPersistence(domain: string, signal?: AbortSignal): Promise<QnsDomainLookup> {
  const result = await fetchLookup(domain, signal);
  setCached(domain, result.owner, result.domainType);
  return result;
}

function buildInitialData(domain: string): { data: QnsDomainLookup; updatedAt: number } | undefined {
  const cached = getCached(domain);
  if (!cached) return undefined;
  const ttl = cached.owner ? HIT_TTL_MS : MISS_TTL_MS;
  return {
    data: { domain, owner: cached.owner, domainType: cached.domainType },
    updatedAt: cached.expiresAt - ttl,
  };
}

export interface UseQnsDomainLookupResult {
  // Resolved owner address (lowercase), or null if not registered / lookup failed.
  owner: string | null;
  // Tier of the resolved domain. Null when owner is null.
  domainType: 'paid' | 'free' | string | null;
  // True while the lookup is still in flight (and we don't have a cached value).
  isPending: boolean;
  // The normalized domain that was actually looked up (lowercase). Empty when
  // the input wasn't a valid .qie domain syntactically.
  normalizedDomain: string;
}

/**
 * Looks up a `.qie` domain name to its owner address.
 *
 * No-ops if:
 *   - the feature flag is off
 *   - the input doesn't syntactically match `<name>.qie`
 *
 * Returns owner=null in all other failure modes (not registered, network
 * error, upstream down) so the caller can simply test `if (owner)` without
 * any try/catch.
 */
export default function useQnsDomainLookup(input: string | undefined | null): UseQnsDomainLookupResult {
  const enabled = QNS_ENABLED && QNS_SEARCH_ENABLED;
  const normalizedDomain = enabled ? (normalizeQnsDomain(input) ?? '') : '';
  const hasValidInput = normalizedDomain.length > 0;
  const initial = hasValidInput ? buildInitialData(normalizedDomain) : undefined;

  const query = useQuery<QnsDomainLookup>({
    queryKey: [ 'qns-lookup', normalizedDomain ],
    queryFn: ({ signal }) => queryFnWithPersistence(normalizedDomain, signal),
    enabled: hasValidInput,
    initialData: initial?.data,
    initialDataUpdatedAt: initial?.updatedAt,
    // The cached entry's TTL is enforced via expiresAt in localStorage.
    // React Query's staleTime mirrors this: HIT_TTL_MS for hits, MISS_TTL_MS
    // for misses. Using the longer one here is safe because expiry happens
    // via the buildInitialData check anyway.
    staleTime: HIT_TTL_MS,
    gcTime: HIT_TTL_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  return React.useMemo(() => ({
    owner: query.data?.owner ?? null,
    domainType: query.data?.domainType ?? null,
    isPending: hasValidInput && query.isFetching && !query.data,
    normalizedDomain,
  }), [ query.data, query.isFetching, normalizedDomain, hasValidInput ]);
}